
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ImagesStepProps {
  data: any;
  onChange: (field: string, value: any) => void;
}

const ImagesStep = ({ data, onChange }: ImagesStepProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  
  const images = data.images || [];

  const uploadImageToStorage = async (file: File): Promise<{ url: string; storage_path: string } | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('property-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('property-images')
        .getPublicUrl(filePath);
      
      return {
        url: publicUrl,
        storage_path: filePath
      };
    } catch (error) {
      return null;
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    
    const newFiles = Array.from(files).slice(0, 10 - images.length);
    setUploading(true);

    try {
      const uploadPromises = newFiles.map(async (file) => {
        const uploadResult = await uploadImageToStorage(file);
        if (uploadResult) {
          return {
            file,
            url: uploadResult.url,
            storage_path: uploadResult.storage_path,
            size: file.size,
            type: file.type
          };
        }
        return null;
      });

      const uploadedImages = await Promise.all(uploadPromises);
      const successfulUploads = uploadedImages.filter(img => img !== null);
      
      if (successfulUploads.length > 0) {
        onChange('images', [...images, ...successfulUploads]);
        toast({
          title: "تم رفع الصور",
          description: `تم رفع ${successfulUploads.length} صورة بنجاح`,
        });
      }

      if (successfulUploads.length < newFiles.length) {
        toast({
          title: "تحذير",
          description: "فشل في رفع بعض الصور",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error handling files:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في رفع الصور",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeImage = async (index: number) => {
    const imageToRemove = images[index];
    
    // حذف الصورة من Storage إذا كانت مرفوعة
    if (imageToRemove.storage_path) {
      try {
        await supabase.storage
          .from('property-images')
          .remove([imageToRemove.storage_path]);
        console.log('Image removed from storage:', imageToRemove.storage_path);
      } catch (error) {
        console.error('Error removing image from storage:', error);
      }
    }

    const newImages = images.filter((_: any, i: number) => i !== index);
    onChange('images', newImages);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
          صور العقار
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* منطقة رفع الصور */}
        <div>
          <Label>رفع صور العقار (حد أقصى 10 صور)</Label>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Upload className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900">اسحب الصور هنا</p>
                <p className="text-sm text-gray-500">أو انقر لاختيار الصور</p>
              </div>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                id="images-input"
                disabled={uploading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('images-input')?.click()}
                disabled={uploading || images.length >= 10}
              >
                {uploading ? 'جار الرفع...' : 'اختيار الصور'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-right">
            يمكنك رفع حتى 10 صور بصيغة JPG, PNG, GIF (الحد الأقصى لكل صورة 5MB)
          </p>
        </div>

        {/* عرض الصور المختارة */}
        {images.length > 0 && (
          <div>
            <h3 className="text-md font-medium mb-3 text-right">الصور المختارة ({images.length}/10)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image: any, index: number) => (
                <div key={index} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={image.url}
                      alt={`صورة ${index + 1}`}
                      className="w-full h-full object-cover"
                      onLoad={() => console.log('Image preview loaded:', image.url)}
                      onError={(e) => {
                        console.error('Image preview failed to load:', image.url);
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                      }}
                    />
                    <div className={`absolute inset-0 flex flex-col items-center justify-center text-center text-gray-500 hidden placeholder`}>
                      <ImageIcon className="h-8 w-8 mb-1" />
                      <p className="text-xs">فشل تحميل الصورة</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="bg-black/50 text-white text-xs p-1 rounded text-center">
                      {index === 0 ? 'الصورة الرئيسية' : `صورة ${index + 1}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2 text-right">
              الصورة الأولى ستكون الصورة الرئيسية للعقار
            </p>
          </div>
        )}

        {/* نصائح حول الصور */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2 text-right">نصائح لصور أفضل:</h4>
          <ul className="text-sm text-blue-800 space-y-1 text-right">
            <li>• استخدم إضاءة طبيعية جيدة</li>
            <li>• اختر زوايا مختلفة للغرف</li>
            <li>• تأكد من نظافة المكان قبل التصوير</li>
            <li>• أضف صور للمرافق المهمة (المطبخ، الحمام، الشرفة)</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default ImagesStep;
